import { ProductItem } from "@repo/apis/bag";
import { Product } from "@repo/apis/browse-search-v2";
import { SavedItem } from "@repo/apis/customers-saveditems-v3";
import { RecommendationProduct } from "@repo/apis/recommendations";
import {
  MergedVariant,
  StockPriceMappedProduct,
} from "@/utils/map-product-data";

type RoundFunction = {
  (value: number, decimals: number): string;
};

export const round: RoundFunction = (value, decimals) => {
  return Number(
    Math.round(Number(value + "e" + decimals)) + "e-" + decimals,
  ).toFixed(decimals);
};

export const mapVariants = (product: StockPriceMappedProduct) => {
  const { variants } = product;

  return variants.map((variant, i) => {
    const pricePounds = variant.price.xrp?.value;
    const priceLocal = variant.price.current.value;

    return {
      item_id: `${variant.sku}`,
      productId: `${product.id}`,
      productCode: `${product.productCode}`,
      variantId: `${variant.id}`,
      item_name: product.name,
      index: i + 1,
      item_brand: product.brand.name,
      item_category: product.productType?.name,
      item_variant: variant.colour,
      size: variant.brandSize,
      price: pricePounds,
      priceLocalCurrency: priceLocal,
      seller_code: variant.seller?.id,
      google_business_vertical: "retail",
    };
  });
};

export const mapRecommendations = (
  recommendations: RecommendationProduct[],
) => {
  return recommendations.map((recommendation, i) => {
    const pricePounds = recommendation.price.xrp?.value;
    const priceLocal = recommendation.price.current.value;

    return {
      productId: `${recommendation.id}`,
      productCode: `${recommendation.productCode}`,
      item_name: recommendation.name,
      index: i + 1,
      item_brand: recommendation.brandName,
      price: pricePounds,
      priceLocalCurrency: priceLocal,
    };
  });
};

export const mapProducts = (products: Product[]) => {
  return products.map((product, i) => {
    const pricePounds = product.price.xrp?.value;
    const priceLocal = product.price.current.value;

    return {
      productId: `${product.id}`,
      productCode: `${product.productCode}`,
      item_name: product.name,
      index: i + 1,
      item_brand: product.brandName,
      item_category: product.productType,
      price: pricePounds ?? undefined,
      priceLocalCurrency: priceLocal ?? undefined,
      google_business_vertical: "retail",
    };
  });
};

export const mapProductItems = (products: ProductItem[]) => {
  return products.map((product, i) => {
    const pricePounds = product.price.current.xrp;
    const priceLocal = product.price.current.value;

    return {
      item_id: `${product.sku}`,
      productId: `${product.productId}`,
      productCode: `${product.productCode}`,
      variantId: `${product.variantId}`,
      item_name: product.name,
      index: i + 1,
      item_brand: product.brandName,
      item_variant: product.colour,
      quantity: product.quantity || undefined,
      size: product.size,
      price: pricePounds ?? undefined,
      priceLocalCurrency: priceLocal ?? undefined,
      seller_code: product.seller?.id,
      google_business_vertical: "retail",
    };
  });
};

export const mapWishlistItems = (items: SavedItem[]) => {
  return items.map((item, i) => {
    const pricePounds = item.price.xrp.value;
    const priceLocal = item.price.current.value;

    return {
      productId: `${item.productId}`,
      productCode: `${item.productCode}`,
      variantId: item.variantId ? `${item.variantId}` : undefined,
      item_name: item.title,
      index: i + 1,
      item_brand: item.brand,
      item_variant: item.colour,
      quantity: 1,
      size: item.size ?? undefined,
      price: pricePounds ?? undefined,
      priceLocalCurrency: priceLocal ?? undefined,
      seller_code: item.seller ?? undefined,
      google_business_vertical: "retail",
    };
  });
};

export const getTotalUniqueSizes = ({
  variants,
}: {
  variants: MergedVariant[];
}) =>
  variants.reduce((uniqueSizes, variant) => {
    uniqueSizes.add(variant.brandSize);
    return uniqueSizes;
  }, new Set()).size;

export const getVariantStockStatus = ({
  variants,
}: {
  variants: MergedVariant[];
}) =>
  variants.map((variant) => ({
    variantId: variant.id,
    variantSku: variant.sku,
    stockStatus: variant.isInStock ? "in stock" : "out of stock",
  }));

export const getTotalInStock = ({ variants }: { variants: MergedVariant[] }) =>
  variants.filter((variant) => variant.isInStock === true).length;
